import { useState, useEffect, useCallback } from 'react';
import { templeService } from '../services';

/**
 * Custom hook for fetching and managing temple data
 * @param {Object} options - Options for temple fetching
 * @param {boolean} options.fetchOnMount - Whether to fetch temples on component mount
 * @param {boolean} options.featured - Whether to fetch only featured temples
 * @param {number} options.limit - Maximum number of temples to fetch
 * @returns {Object} Temple data and loading state
 */
const useTemples = ({ 
  fetchOnMount = true, 
  featured = false,
  limit = 0
} = {}) => {
  const [temples, setTemples] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch all temples
   */
  const fetchTemples = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      let data;
      
      if (featured) {
        data = await templeService.fetchFeaturedTemples(limit);
      } else {
        data = await templeService.fetchAllTemples();
      }
      
      setTemples(data);
    } catch (err) {
      console.error('Error fetching temples:', err);
      setError(err.message || 'Failed to fetch temples');
    } finally {
      setLoading(false);
    }
  }, [featured, limit]);

  /**
   * Fetch a single temple by ID
   * @param {string} templeId - ID of the temple to fetch
   * @returns {Promise<Object>} Temple data
   */
  const fetchTempleById = useCallback(async (templeId) => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await templeService.fetchTempleById(templeId);
      return data;
    } catch (err) {
      console.error(`Error fetching temple ${templeId}:`, err);
      setError(err.message || `Failed to fetch temple ${templeId}`);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch temples on mount if requested
  useEffect(() => {
    if (fetchOnMount) {
      fetchTemples();
    }
  }, [fetchOnMount, fetchTemples]);

  return {
    temples,
    loading,
    error,
    fetchTemples,
    fetchTempleById
  };
};

export default useTemples;
